import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";
import CUSTOMERS from "../api/services/CUSTOMERS";
import PLANS from "../api/services/PLANS";
import APPOINTMENTS from "../api/services/APPOINTMENTS";
import Layout from "../layouts/layout";
import MembershipCard from "../containers/_clientMembership/membershipCard";
import CommentModal from "../components/commentModal";
import { ConfirmModal } from "../components/confirmModal";
import Spinner from "../components/spinner";
import Loader from "../components/loader";
import { redirectToWhatsapp } from "../utils/redirectToWhatsapp";
import { calculateEndTimeOfAppointment } from "../utils/calculateEndTimeOfAppointment";
import Lottie from "lottie-react";
import { useBusinessCapabilities } from "../hooks/useBusinessCapabilities";
import BILLING from "../api/services/BILLING";
import cashRegisterLottie from "../assets/lottie-cash-register.json";
import { paths } from "../routes/paths";
import styles from "../styles/_screens/clientMembership.module.scss";

const ClientMembership = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Active tab
  const [activeTab, setActiveTab] = useState("details");

  // Client data (editable)
  const [clientData, setClientData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Plans
  const [allPlans, setAllPlans] = useState([]);
  const [userPlans, setUserPlans] = useState([]);
  const [resSuccess, setResSuccess] = useState(null);

  // Assign plan state
  const [currentServicePlanId, setCurrentServicePlanId] = useState("");
  const [servicePlans, setServicePlans] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFetched, setHistoryFetched] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Comment/confirm modals for history
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalLoading, setConfirmModalLoading] = useState(false);

  // Business capabilities (pro + clearing)
  const { isPro, hasClearingSetup, canUseBilling, loading: capLoading } = useBusinessCapabilities();

  // Billing state
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesFetched, setInvoicesFetched] = useState(false);

  // Gender options
  const genderOptions = [
    { value: "male", label: "זכר" },
    { value: "female", label: "נקבה" },
    { value: "other", label: "אחר" },
  ];

  const formatDateInput = (d) => {
    if (!d) return "";
    try {
      const dt = new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    } catch {
      return "";
    }
  };

  const getFirstLetter = (name) => {
    if (!name) return "?";
    const first = name.charAt(0);
    if (/[A-Za-z֐-׿]/.test(first)) return first.toUpperCase();
    return "?";
  };

  // Fetch client details
  const fetchClient = useCallback(async () => {
    if (!id) return;
    try {
      const res = await CUSTOMERS.get_by_id(id);
      const client = res?.data?.userId || res?.data || res;
      const customerData = res?.data || {};
      setClientData({
        userId: client._id || id,
        name: client.name || "",
        phone: client.phone || "",
        email: client.email || "",
        profile: client.profile || "",
        idNumber: client.idNumber || "",
        address: client.address || "",
        dateOfBirth: client.dateOfBirth || "",
        gender: client.gender || "",
        isHealthCheckCompleted: customerData.isHealthCheckCompleted || false,
      });
    } catch (error) {
      console.log("Error fetching client:", error);
    }
  }, [id]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!id) return;
    setStatsLoading(true);
    try {
      const res = await CUSTOMERS.get_stats(id);
      setStats(res?.data || res);
    } catch (error) {
      console.log("Error fetching stats:", error);
    }
    setStatsLoading(false);
  }, [id]);

  // Fetch all plans (for assignment dropdown)
  const fetchAllPlans = useCallback(async () => {
    try {
      const res = await PLANS.get_all_plans();
      setAllPlans(res.data.plans || []);
    } catch (error) {
      console.log("Error fetching plans:", error);
    }
  }, []);

  // Fetch user plans
  const fetchUserPlans = useCallback(async () => {
    try {
      const res = await PLANS.get_user_plans({ userId: id });
      setUserPlans(res.data.data.attachedPlans || []);
    } catch (error) {
      console.log("Error fetching user plans:", error);
    }
  }, [id]);

  useEffect(() => {
    fetchClient();
    fetchStats();
    fetchAllPlans();
  }, [fetchClient, fetchStats, fetchAllPlans]);

  // Refetch user plans on success
  useEffect(() => {
    setResSuccess(null);
    fetchUserPlans();
  }, [resSuccess, fetchUserPlans]);

  // Update field helper
  const updateField = (key, value) => {
    setClientData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  // Save all changes
  const saveAllChanges = async () => {
    setSaving(true);
    try {
      await CUSTOMERS.create_client({
        userId: clientData.userId,
        name: clientData.name,
        phone: clientData.phone,
        email: clientData.email,
        idNumber: clientData.idNumber,
        address: clientData.address,
        dateOfBirth: clientData.dateOfBirth,
        gender: clientData.gender,
      });
      toast.success("פרטי הלקוח נשמרו");
      setHasChanges(false);
    } catch (err) {
      console.log(err);
      toast.error("שגיאה בשמירת פרטים");
    }
    setSaving(false);
  };

  // Assign plan
  const assignPlan = async () => {
    const payload = {
      servicePlanIds: servicePlans.map((i) => i._id),
      userId: clientData?.userId,
    };
    setAssignLoading(true);
    try {
      const res = await PLANS.assign_plans(payload);
      setServicePlans([]);
      setCurrentServicePlanId("");
      toast.success(res.data.message);
      setResSuccess(true);
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message || "שגיאה");
    }
    setAssignLoading(false);
  };

  // Fetch history (lazy — only when tab is opened)
  const businessId = JSON.parse(localStorage.getItem("user_id"));

  const fetchHistory = useCallback(async (page = 1, from, to) => {
    setHistoryLoading(true);
    try {
      const payload = { id: businessId, userId: id, page, size: 20 };
      if (from) payload.startDate = from;
      if (to) payload.endDate = to;
      const res = await CUSTOMERS.get_client_history(payload);
      const items = res.data?.data || [];
      if (page === 1) {
        setHistory(items);
      } else {
        setHistory((prev) => [...prev, ...items]);
      }
      setHistoryTotalPages(res.data?.totalPages || 1);
      setHistoryFetched(true);
    } catch (err) {
      console.log(err);
    }
    setHistoryLoading(false);
  }, [id, businessId]);

  // Cancel appointment from history
  const cancelHistoryAppointment = async () => {
    setConfirmModalLoading(true);
    try {
      const res = await APPOINTMENTS.cancel_appointment({ id: isConfirmModalOpen, status: 3 });
      toast.success(res.data.message);
      setIsConfirmModalOpen(false);
      fetchHistory(1, dateFrom, dateTo);
    } catch (err) {
      toast.error(err?.response?.data?.message || "שגיאה");
    }
    setConfirmModalLoading(false);
  };

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const res = await BILLING.get_invoices_list({ clientName: clientData?.name });
      setInvoices(res?.data?.data || []);
      setInvoicesFetched(true);
    } catch (err) {
      console.log(err);
    }
    setInvoicesLoading(false);
  }, [clientData?.name]);

  // Tabs configuration
  const tabs = [
    { key: "details", label: "פרטים אישיים" },
    { key: "memberships", label: "מנויים וכרטיסיות" },
    { key: "history", label: "היסטוריית תורים" },
    { key: "billing", label: "הנהלת חשבונות" },
  ];

  // Loading state
  if (!clientData) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.pageWrapper}>
            <Loader />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.pageWrapper}>

          {/* ── Client Header ── */}
          <div className={styles.clientHeader}>
            <div className={styles.headerLeft}>
              {clientData.profile ? (
                <img
                  src={clientData.profile}
                  alt={clientData.name}
                  className={styles.avatar}
                />
              ) : (
                <div className={styles.avatarPlaceholder}>
                  {getFirstLetter(clientData.name)}
                </div>
              )}
              <div className={styles.headerInfo}>
                <h1 className={styles.clientName}>{clientData.name || "לקוח"}</h1>
                {clientData.phone && (
                  <span className={styles.clientPhone}>{clientData.phone}</span>
                )}
              </div>
            </div>
            <div className={styles.headerActions}>
              {clientData.phone && (
                <>
                  <a
                    href={`tel:${clientData.phone}`}
                    className={styles.actionBtn}
                    title="התקשר"
                  >
                    <img src="/assets/phone-icon-1.svg" alt="call" />
                    <span>התקשר</span>
                  </a>
                  <button
                    className={`${styles.actionBtn} ${styles.actionBtnGreen}`}
                    onClick={() => redirectToWhatsapp(clientData.phone)}
                    title="WhatsApp"
                  >
                    <img src="/assets/whatsapp-icon-1.svg" alt="whatsapp" />
                    <span>WhatsApp</span>
                  </button>
                </>
              )}
              <button
                className={`${styles.actionBtn} ${styles.actionBtnBrand}`}
                onClick={() =>
                  navigate(paths.createAppointment, {
                    state: {
                      clientId: clientData.userId,
                      clientName: clientData.name,
                      clientPhone: clientData.phone,
                    },
                  })
                }
              >
                <img src="/assets/add-icon-1.svg" alt="add" />
                <span>קביעת תור</span>
              </button>
            </div>
          </div>

          {/* ── Tab Navigation ── */}
          <div className={styles.tabNav}>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`${styles.tabBtn} ${activeTab === tab.key ? styles.tabBtnActive : ""}`}
                onClick={() => {
                  setActiveTab(tab.key);
                  if (tab.key === "history" && !historyFetched) {
                    fetchHistory(1);
                  }
                  if (tab.key === "billing" && !invoicesFetched && canUseBilling) {
                    fetchInvoices();
                  }
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Tab Content ── */}
          <div className={styles.tabContent}>

            {/* ── Personal Details Tab ── */}
            {activeTab === "details" && (
              <div className={styles.detailsTab}>
                {/* Personal Details Card */}
                <div className={styles.card}>
                  <div className={styles.cardTitle}>פרטים אישיים</div>
                  <div className={styles.fieldsGrid}>
                    <div className={styles.fieldItem}>
                      <label className={styles.fieldLabel}>שם</label>
                      <input
                        className={styles.fieldInput}
                        value={clientData.name}
                        onChange={(e) => updateField("name", e.target.value)}
                        placeholder="שם מלא"
                      />
                    </div>
                    <div className={styles.fieldItem}>
                      <label className={styles.fieldLabel}>טלפון</label>
                      <input
                        className={styles.fieldInput}
                        value={clientData.phone}
                        onChange={(e) => updateField("phone", e.target.value)}
                        placeholder="050..."
                        dir="ltr"
                      />
                    </div>
                    <div className={styles.fieldItem}>
                      <label className={styles.fieldLabel}>אימייל</label>
                      <input
                        className={styles.fieldInput}
                        value={clientData.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        placeholder="email@example.com"
                        dir="ltr"
                      />
                    </div>
                    <div className={styles.fieldItem}>
                      <label className={styles.fieldLabel}>ת.ז</label>
                      <input
                        className={styles.fieldInput}
                        value={clientData.idNumber}
                        onChange={(e) => updateField("idNumber", e.target.value)}
                        placeholder="מספר זהות"
                        dir="ltr"
                      />
                    </div>
                    <div className={`${styles.fieldItem} ${styles.fieldFull}`}>
                      <label className={styles.fieldLabel}>כתובת</label>
                      <input
                        className={styles.fieldInput}
                        value={clientData.address}
                        onChange={(e) => updateField("address", e.target.value)}
                        placeholder="כתובת מלאה"
                      />
                    </div>
                    <div className={styles.fieldItem}>
                      <label className={styles.fieldLabel}>תאריך לידה</label>
                      <input
                        type="date"
                        className={styles.fieldInput}
                        value={formatDateInput(clientData.dateOfBirth)}
                        onChange={(e) => updateField("dateOfBirth", e.target.value)}
                      />
                    </div>
                    <div className={styles.fieldItem}>
                      <label className={styles.fieldLabel}>מגדר</label>
                      <select
                        className={styles.fieldInput}
                        value={clientData.gender || ""}
                        onChange={(e) => updateField("gender", e.target.value)}
                      >
                        <option value="">לא צוין</option>
                        {genderOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {hasChanges && (
                    <div className={styles.saveBtnWrap}>
                      <button
                        className={styles.saveBtn}
                        onClick={saveAllChanges}
                        disabled={saving}
                      >
                        {saving ? "שומר..." : "שמירת שינויים"}
                      </button>
                    </div>
                  )}
                </div>

                {/* Health Declaration */}
                <div className={styles.card}>
                  <div className={styles.cardTitle}>הצהרת בריאות</div>
                  <div className={styles.healthStatus}>
                    <span
                      className={`${styles.healthBadge} ${
                        clientData.isHealthCheckCompleted
                          ? styles.healthBadgeGreen
                          : styles.healthBadgeRed
                      }`}
                    >
                      {clientData.isHealthCheckCompleted ? "מאושר" : "לא קיים"}
                    </span>
                  </div>
                </div>

                {/* Statistics Card */}
                <div className={styles.card}>
                  <div className={styles.cardTitle}>
                    <img src="/assets/chart.png" alt="" width={16} />
                    סטטיסטיקות
                  </div>
                  {statsLoading ? (
                    <div className={styles.loadingState}>טוען נתונים...</div>
                  ) : (
                    <div className={styles.statsGrid}>
                      <div className={styles.statItem}>
                        <span className={`${styles.statValue} ${styles.statValueBrand}`}>
                          {stats?.totalAppointments ?? 0}
                        </span>
                        <span className={styles.statLabel}>סה"כ תורים</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={`${styles.statValue} ${styles.statValueGreen}`}>
                          {stats?.completedAppointments ?? 0}
                        </span>
                        <span className={styles.statLabel}>הושלמו</span>
                      </div>
                      <div className={styles.statItem}>
                        <span className={`${styles.statValue} ${styles.statValueRed}`}>
                          {stats?.cancelledAppointments ?? 0}
                        </span>
                        <span className={styles.statLabel}>בוטלו</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Memberships Tab ── */}
            {activeTab === "memberships" && (
              <div className={styles.membershipsTab}>
                {/* Assign Plan Card */}
                {allPlans?.length > 0 && (
                  <div className={styles.card}>
                    <div className={styles.cardTitle}>שייך מנוי ללקוח</div>
                    <div className={styles.assignSection}>
                      <div className={styles.assignRow}>
                        <select
                          className={styles.assignSelect}
                          value={currentServicePlanId}
                          onChange={(e) => {
                            setCurrentServicePlanId(e.target.value);
                            if (!servicePlans.find((x) => x._id === e.target.value)) {
                              setServicePlans((prev) => [
                                ...prev,
                                allPlans.find((j) => j._id === e.target.value),
                              ]);
                            }
                          }}
                        >
                          <option value="" disabled>
                            בחר מנוי
                          </option>
                          {allPlans.map((i) => (
                            <option value={i._id} key={i._id}>
                              {i.name}
                            </option>
                          ))}
                        </select>
                        <button
                          className={styles.assignBtn}
                          onClick={assignPlan}
                          disabled={servicePlans.length === 0}
                        >
                          {assignLoading ? <Spinner /> : "שייך"}
                        </button>
                      </div>
                      {servicePlans.length > 0 && (
                        <div className={styles.selectedPlans}>
                          {servicePlans.map((i) => (
                            <span
                              key={i._id}
                              className={styles.selectedPlanTag}
                              onClick={() =>
                                setServicePlans((prev) =>
                                  prev.filter((p) => p._id !== i._id)
                                )
                              }
                            >
                              {i.name} &times;
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Active Memberships */}
                <div className={styles.card}>
                  <div className={styles.cardTitle}>מנויים משוייכים ללקוח</div>
                  {userPlans?.length > 0 ? (
                    <div className={styles.plansGrid}>
                      {userPlans.map((i) => (
                        <MembershipCard
                          key={i._id}
                          id={i._id}
                          userId={id}
                          title={i.name}
                          price={i.price}
                          isFrozen={i.isFrozen}
                          disabled={i.isFrozen}
                          createdAt={i.createdAt}
                          expiresAt={i.expiresAt}
                          numberOfTurns={i.numberOfTurns}
                          isSubscription={i.isSubscription}
                          consumedOneTimeTurns={i.consumedOneTimeTurns}
                          setResSuccess={setResSuccess}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <p>לא נמצאו מנויים פעילים ללקוח/ה</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── History Tab ── */}
            {activeTab === "history" && (
              <div className={styles.historyTab}>
                {/* Date filters */}
                <div className={styles.dateFilters}>
                  <div className={styles.dateFilterItem}>
                    <label>מתאריך</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                  <div className={styles.dateFilterItem}>
                    <label>עד תאריך</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                  <button
                    className={styles.filterBtn}
                    onClick={() => {
                      setHistoryPage(1);
                      fetchHistory(1, dateFrom, dateTo);
                    }}
                  >
                    סינון
                  </button>
                  {(dateFrom || dateTo) && (
                    <button
                      className={styles.clearFilterBtn}
                      onClick={() => {
                        setDateFrom("");
                        setDateTo("");
                        setHistoryPage(1);
                        fetchHistory(1, "", "");
                      }}
                    >
                      נקה
                    </button>
                  )}
                </div>

                {/* History list */}
                {historyLoading && history.length === 0 ? (
                  <Loader />
                ) : history.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>לא קיימת היסטוריית תורים</p>
                  </div>
                ) : (
                  <>
                    <div className={styles.historyList}>
                      {history.map((appt) => {
                        const endTime = appt.serviceId?.time
                          ? calculateEndTimeOfAppointment(appt.timeSlot, appt.serviceId.time)
                          : "";
                        const statusLabel =
                          appt.status === 3
                            ? "בוטל"
                            : appt.isConfirmed === "CONFIRMED"
                            ? "אושר"
                            : "ממתין";
                        const statusClass =
                          appt.status === 3
                            ? styles.statusCancelled
                            : appt.isConfirmed === "CONFIRMED"
                            ? styles.statusConfirmed
                            : styles.statusPending;

                        return (
                          <div key={appt._id} className={styles.historyRow}>
                            <div className={styles.historyTime}>
                              <span className={styles.historyTimeSlot}>
                                {appt.timeSlot}
                              </span>
                              <span className={styles.historyEndTime}>
                                {endTime || ""}
                              </span>
                            </div>
                            <div className={styles.historyInfo}>
                              <span className={styles.historyService}>
                                {appt.serviceId?.name || ""}
                              </span>
                              <span className={styles.historyDate}>
                                {format(new Date(appt.date), "dd/MM/yyyy")}
                              </span>
                            </div>
                            <span className={`${styles.historyStatus} ${statusClass}`}>
                              {statusLabel}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {historyPage < historyTotalPages && (
                      <button
                        className={styles.loadMoreBtn}
                        onClick={() => {
                          const next = historyPage + 1;
                          setHistoryPage(next);
                          fetchHistory(next, dateFrom, dateTo);
                        }}
                        disabled={historyLoading}
                      >
                        {historyLoading ? "טוען..." : "טען עוד"}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── Billing Tab ── */}
            {activeTab === "billing" && (
              <div className={styles.billingTab}>
                {!canUseBilling ? (
                  <div className={styles.proGate}>
                    <Lottie
                      animationData={cashRegisterLottie}
                      loop={true}
                      style={{ width: 120, height: 120 }}
                    />
                    <h3>
                      {!isPro
                        ? "פיצ'ר זה זמין למנויי Pro בלבד"
                        : "סליקה לא מוגדרת"}
                    </h3>
                    <p>
                      {!isPro
                        ? "שדרגו את המנוי שלכם כדי לגשת להנהלת חשבונות, חיובים וחשבוניות"
                        : "יש להגדיר סליקת אשראי בהגדרות התשלום לפני שניתן לחייב לקוחות"}
                    </p>
                    <button
                      className={styles.proGateBtn}
                      onClick={() =>
                        navigate(!isPro ? paths.subscription : paths.paymentSettings)
                      }
                    >
                      {!isPro ? "שדרוג למנוי Pro" : "הגדרת סליקה"}
                    </button>
                  </div>
                ) : (
                  <>
                    {invoicesLoading ? (
                      <Loader />
                    ) : invoices.length === 0 ? (
                      <div className={styles.emptyState}>
                        <p>לא נמצאו חשבוניות ללקוח/ה</p>
                      </div>
                    ) : (
                      <div className={styles.historyList}>
                        {invoices.map((inv, idx) => (
                          <div key={inv._id || idx} className={styles.historyRow}>
                            <div className={styles.historyInfo}>
                              <span className={styles.historyService}>
                                {inv.docType || inv.type || "חשבונית"}
                              </span>
                              <span className={styles.historyDate}>
                                {inv.date
                                  ? format(new Date(inv.date), "dd/MM/yyyy")
                                  : ""}
                              </span>
                            </div>
                            <span className={styles.invoiceAmount}>
                              {inv.amount != null ? `₪${inv.amount}` : ""}
                            </span>
                            <span
                              className={`${styles.historyStatus} ${
                                inv.status === "paid" || inv.status === "PAID"
                                  ? styles.statusConfirmed
                                  : styles.statusPending
                              }`}
                            >
                              {inv.status === "paid" || inv.status === "PAID"
                                ? "שולם"
                                : "ממתין"}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Modals */}
      {isCommentModalOpen && (
        <CommentModal
          setIsOpen={setIsCommentModalOpen}
          appointmentId={isCommentModalOpen}
        />
      )}
      {isConfirmModalOpen && (
        <ConfirmModal
          setIsOpen={setIsConfirmModalOpen}
          handleClick={cancelHistoryAppointment}
          loading={confirmModalLoading}
        />
      )}
    </Layout>
  );
};

export default ClientMembership;
